import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { Input } from '@/components/ui/input';
import { FormSubmitButton } from '@/components/buttons/form-submit-button';
import { Eye, EyeOff } from 'lucide-react';
import { buildUrl } from '@salesforce/storefront-next-runtime/site-context';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { useCurrentSiteAndLocaleRef } from '@/hooks/use-current-site-and-locale-ref';
import { cn } from '@/lib/utils';
import AuthStepShell from '../auth-step-shell';
import { useLoginFlowVariant } from '../variant';

interface PasswordStepProps {
    email: string;
    returnUrl: string;
    onBack: () => void;
    onSuccess: () => void;
}

type LoginActionResponse = {
    success: boolean;
    error?: string;
};

export default function PasswordStep({ email, returnUrl, onBack, onSuccess }: PasswordStepProps): ReactElement {
    const { t } = useTranslation('login');
    const fetcher = useFetcher<LoginActionResponse>();
    const [showPassword, setShowPassword] = useState(false);
    const config = useConfig<AppConfig>();
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();
    const isPage = useLoginFlowVariant() === 'page';
    const loginAction = useMemo(
        () => buildUrl({ to: '/login', urlConfig: config.url, params: { siteId: siteRef, localeId: localeRef } }),
        [config.url, siteRef, localeRef]
    );

    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data) return;
        if (fetcher.data.success !== false) {
            onSuccess();
        }
    }, [fetcher.data, fetcher.state, onSuccess]);

    const error = fetcher.data?.success === false ? fetcher.data.error : undefined;
    const inputClass = cn(isPage && 'h-12 text-base sm:h-14');

    return (
        <AuthStepShell heading={t('signIn', { defaultValue: 'Sign In' })} onBack={onBack} step={2}>
            <fetcher.Form method="post" action={loginAction} className="space-y-6">
                {error ? (
                    <div className="rounded bg-destructive/10 border border-destructive/20 px-4 py-3 text-destructive">
                        {error}
                    </div>
                ) : null}

                <div>
                    <label htmlFor="sheet-login-email" className="block text-sm font-medium text-foreground">
                        {t('emailLabel', { defaultValue: 'Email address' })}
                    </label>
                    <Input
                        id="sheet-login-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        readOnly
                        className={cn('mt-1 bg-muted', inputClass)}
                        defaultValue={email}
                    />
                </div>

                <div>
                    <label htmlFor="sheet-login-password" className="block text-sm font-medium text-foreground">
                        {t('passwordLabel', { defaultValue: 'Password' })}
                    </label>
                    <div className="relative mt-1">
                        <Input
                            id="sheet-login-password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            required
                            placeholder={t('passwordPlaceholder', { defaultValue: 'Enter your password' })}
                            className={cn('pr-10 [&::-ms-clear]:hidden [&::-ms-reveal]:hidden', inputClass)}
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3 text-muted-foreground hover:text-foreground"
                            onClick={() => setShowPassword((prev) => !prev)}
                            tabIndex={-1}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}>
                            {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                        </button>
                    </div>
                </div>

                <input type="hidden" name="loginMode" value="password" />
                <input type="hidden" name="returnUrl" value={returnUrl} />

                {config.features.b2b.enabled ? null : (
                    <div className="text-left">
                        <Link to="/forgot-password" className="text-sm text-primary hover:text-primary/80">
                            {t('sheet.forgotPassword', { defaultValue: 'Forgot Password?' })}
                        </Link>
                    </div>
                )}

                <FormSubmitButton
                    defaultText={t('signIn', { defaultValue: 'Sign In' })}
                    submittingText={t('signingIn', { defaultValue: 'Signing in...' })}
                    fetcher={fetcher}
                    className={cn('w-full', isPage && 'h-12 text-base sm:h-14')}
                />

                <div className="text-center">
                    <button
                        type="button"
                        className="cursor-pointer text-sm text-foreground underline underline-offset-2"
                        onClick={onBack}>
                        {t('sheet.tryAnotherWay', { defaultValue: 'Try another way to sign in' })}
                    </button>
                </div>
            </fetcher.Form>
        </AuthStepShell>
    );
}
